import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Editor from '@monaco-editor/react';
import { ContentMarkdownRenderer as MarkdownRenderer, ContentStudio } from '../../content-studio/ContentStudio';
import InteractiveQuiz from '../InteractiveQuiz/InteractiveQuiz';
import { splitMarkdownWithQuizBlocks } from '../../utils/quizBlockParser';
import { Maximize2, Minimize2 } from 'lucide-react';
// import { useCurriculumStore } from '../../../store/curriculumStore'; // 이 줄이 있다면 제거 또는 주석
// 모바일 뷰 감지용 훅 추가
function useIsMobile(breakpoint = 768) {
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
    useEffect(()=>{
        const onResize = ()=>setIsMobile(window.innerWidth < breakpoint);
        window.addEventListener('resize', onResize);
        return ()=>window.removeEventListener('resize', onResize);
    }, [
        breakpoint
    ]);
    return isMobile;
}
// 스타일 컴포넌트 (LectureEditForm/index.tsx에서 가져오거나 여기서 직접 정의)
// 예시: LectureEditForm/styles.ts 와 같은 파일로 분리 후 import
// 여기서는 간단하게 필요한 스타일만 직접 정의하거나, props로 스타일 객체를 받을 수도 있습니다.
// 우선은 LectureEditForm/index.tsx에 정의된 스타일 컴포넌트 이름을 그대로 사용한다고 가정합니다.
// 실제 구현 시에는 이 부분의 스타일 정의 또는 import를 명확히 해야 합니다.
const MarkdownEditorContainer = styled.div`
  display: flex;
  position: relative;
  min-height: 600px; 
  border: 1px solid #e5e7eb; 
  border-radius: 4px;
  overflow: hidden; 
  
  @media (max-width: 768px) {
    flex-direction: column;
    min-height: 800px;
  }
`;
const EditorColumn = styled.div`
  flex: 1; 
  min-width: 0; 
  display: flex; 
  flex-direction: column;
`;
const PreviewColumn = styled.div`
  flex: 1; 
  min-width: 0;
  border-left: 1px solid #e5e7eb; 
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    border-left: none;
    border-top: 1px solid #e5e7eb;
  }
`;
const Label = styled.label`
  display: block; 
  margin-bottom: 8px; 
  font-weight: bold; 
  font-size: 0.9rem;
`;
const PreviewContentArea = styled.div` // PreviewContainer 대신 사용될 수 있음
  flex-grow: 1; 
  border: none; 
  border-radius: 0;
  background-color: #f9fafb;
  padding: 15px;
  min-height: auto;
  overflow-y: auto; // MarkdownRenderer 내용이 길어질 경우 스크롤

  h1, h2, h3, h4, h5, h6 { margin-top: 1em; margin-bottom: 0.5em; color: #111; }
  p { margin-bottom: 0.8em; line-height: 1.6; }
  ul, ol { margin-left: 20px; margin-bottom: 0.8em; }
  li { margin-bottom: 0.3em; }
  code { background-color: #eee; padding: 0.2em 0.4em; border-radius: 3px; font-family: monospace; }
  pre code { background-color: transparent; padding: 0; }
  pre { background-color: #eee; padding: 10px; border-radius: 4px; overflow-x: auto; }
  blockquote { border-left: 4px solid #ccc; padding-left: 10px; color: #666; margin-left: 0; }
`;
const QuizPreviewNotice = styled.p`
  margin-top: 8px;
  font-size: 0.75rem;
  color: #6b7280;
`;
const QuizFallback = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.85rem;
  color: #1f2937;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  padding: 12px;
`;
const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 15px 0;
  margin-bottom: 5px;
`;
const ExpandButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #1f2937;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5f5;
  }
`;
const ExpandedPreviewOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;
const ExpandedPreviewContent = styled.div`
  background: #ffffff;
  width: min(100%, 960px);
  max-height: 90vh;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 30px 60px rgba(15, 23, 42, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;
const ExpandedPreviewBody = styled.div`
  padding: 24px 28px;
  overflow-y: auto;
  background: #f8fafc;

  @media (max-width: 768px) {
    padding: 18px 20px;
  }
`;
const MarkdownEditorModeComponent = /*#__PURE__*/ React.memo(({ markdownText, previewContent, editorOptions, onMarkdownChange, onEditorMount })=>{
    const isMobile = useIsMobile();
    // const updateSelectedLessonMarkdown = useCurriculumStore(state => state.updateSelectedLessonMarkdown); // 제거 또는 주석
    // 입력 시 상위 컴포넌트(react-hook-form Controller)로만 변경 알림
    const handleMarkdownChange = (value)=>{
        onMarkdownChange(value);
    // updateSelectedLessonMarkdown(value || ''); // <<< 이 부분을 반드시 제거하거나 주석 처리!
    };
    const previewSource = previewContent || markdownText || '';
    const previewSegments = React.useMemo(()=>{
        const segments = splitMarkdownWithQuizBlocks(previewSource);
        if (!segments.length) {
            return previewSource ? [
                {
                    type: 'markdown',
                    content: previewSource
                }
            ] : [];
        }
        return segments;
    }, [
        previewSource
    ]);
    const [isPreviewExpanded, setPreviewExpanded] = React.useState(false);
    const [visualMode, setVisualMode] = React.useState(false);
    const studio = React.useRef(null);
    const [leavingStudio, setLeavingStudio] = React.useState(false);
    const [studioError, setStudioError] = React.useState('');
    const leaveStudio = async ()=>{
        if (leavingStudio) return;
        setLeavingStudio(true);
        setStudioError('');
        try {
            if (!studio.current) throw new Error('편집기를 준비한 뒤 다시 시도해 주세요.');
            await studio.current.flush();
            setVisualMode(false);
        } catch (error) {
            setStudioError(error instanceof Error ? error.message : '마지막 변경 사항을 반영하지 못했습니다. 다시 시도해 주세요.');
        } finally{
            setLeavingStudio(false);
        }
    };
    const togglePreviewExpanded = ()=>setPreviewExpanded((prev)=>!prev);
    if (visualMode) return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                disabled: leavingStudio,
                onClick: ()=>{
                    void leaveStudio();
                },
                children: leavingStudio ? '마지막 변경 사항 반영 중…' : '마크다운 편집으로 돌아가기'
            }),
            studioError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: studioError
            }),
            /*#__PURE__*/ _jsx(ContentStudio, {
                ref: studio,
                markdown: markdownText,
                onChange: onMarkdownChange
            })
        ]
    });
    return /*#__PURE__*/ _jsxs(MarkdownEditorContainer, {
        children: [
            /*#__PURE__*/ _jsxs(EditorColumn, {
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setVisualMode(true),
                        children: "템플릿 제작실에서 편집"
                    }),
                    /*#__PURE__*/ _jsx(Label, {
                        style: {
                            padding: '10px 15px 0',
                            marginBottom: '5px'
                        },
                        children: "마크다운 편집"
                    }),
                    /*#__PURE__*/ _jsx(Editor, {
                        height: isMobile ? '300px' : '100%',
                        language: "markdown",
                        value: markdownText,
                        onMount: (editor, monaco)=>onEditorMount(editor, monaco, 'main-editor'),
                        onChange: handleMarkdownChange,
                        options: {
                            ...editorOptions,
                            scrollbar: {
                                vertical: 'auto',
                                horizontal: 'auto'
                            },
                            minimap: {
                                enabled: true
                            }
                        },
                        theme: "vs-light"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(PreviewColumn, {
                children: [
                    /*#__PURE__*/ _jsxs(PreviewHeader, {
                        children: [
                            /*#__PURE__*/ _jsx(Label, {
                                style: {
                                    marginBottom: 0
                                },
                                children: "미리보기"
                            }),
                            /*#__PURE__*/ _jsx(ExpandButton, {
                                type: "button",
                                onClick: togglePreviewExpanded,
                                "aria-label": isPreviewExpanded ? '미리보기 축소' : '미리보기 확대',
                                children: isPreviewExpanded ? /*#__PURE__*/ _jsx(Minimize2, {
                                    size: 16
                                }) : /*#__PURE__*/ _jsx(Maximize2, {
                                    size: 16
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(PreviewContentArea, {
                        children: [
                            previewSegments.length === 0 && /*#__PURE__*/ _jsx("p", {
                                style: {
                                    color: '#6b7280',
                                    fontSize: '0.85rem'
                                },
                                children: "미리볼 콘텐츠가 없습니다."
                            }),
                            previewSegments.map((segment, index)=>{
                                if (segment.type === 'quiz') {
                                    if (!segment.questions.length) {
                                        return /*#__PURE__*/ _jsx(QuizFallback, {
                                            children: `퀴즈 블록 구문을 파싱할 수 없습니다. YAML 형식을 확인해주세요.\n\n예시:\n:::quiz\nid: quiz-1\ntype: multiple-choice\nquestion: 예시 문항\noptions:\n  - 선택지 1\n  - 선택지 2\nanswerIndex: 0\n:::`
                                        }, `quiz-fallback-${index}`);
                                    }
                                    return /*#__PURE__*/ _jsxs("div", {
                                        style: {
                                            marginBottom: '24px'
                                        },
                                        children: [
                                            /*#__PURE__*/ _jsx(InteractiveQuiz, {
                                                quizData: segment.questions,
                                                onSubmit: ()=>undefined
                                            }),
                                            /*#__PURE__*/ _jsx(QuizPreviewNotice, {
                                                children: "미리보기용 인터랙티브 퀴즈입니다. 저장 후 학습자 화면에서도 동일한 형태로 표시됩니다."
                                            })
                                        ]
                                    }, `quiz-${index}`);
                                }
                                console.log('🔍 [MarkdownEditorMode] Rendering MarkdownRenderer:', {
                                    index,
                                    content: segment.content?.substring(0, 100),
                                    hasVideoDirective: segment.content?.includes(':::video'),
                                    segmentType: segment.type
                                });
                                return /*#__PURE__*/ _jsx(MarkdownRenderer, {
                                    markdown: segment.content
                                }, `markdown-${index}`);
                            })
                        ]
                    })
                ]
            }),
            isPreviewExpanded && /*#__PURE__*/ _jsx(ExpandedPreviewOverlay, {
                role: "dialog",
                "aria-modal": "true",
                children: /*#__PURE__*/ _jsxs(ExpandedPreviewContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(PreviewHeader, {
                            style: {
                                padding: '16px 20px 0',
                                marginBottom: 0
                            },
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    style: {
                                        marginBottom: 0
                                    },
                                    children: "미리보기"
                                }),
                                /*#__PURE__*/ _jsx(ExpandButton, {
                                    type: "button",
                                    onClick: togglePreviewExpanded,
                                    "aria-label": "미리보기 닫기",
                                    children: /*#__PURE__*/ _jsx(Minimize2, {
                                        size: 16
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(ExpandedPreviewBody, {
                            children: [
                                previewSegments.length === 0 && /*#__PURE__*/ _jsx("p", {
                                    style: {
                                        color: '#94a3b8',
                                        fontSize: '0.9rem'
                                    },
                                    children: "미리볼 콘텐츠가 없습니다."
                                }),
                                previewSegments.map((segment, index)=>{
                                    if (segment.type === 'quiz') {
                                        if (!segment.questions.length) {
                                            return /*#__PURE__*/ _jsx(QuizFallback, {
                                                children: `퀴즈 블록 구문을 파싱할 수 없습니다. YAML 형식을 확인해주세요.\n\n예시:\n:::quiz\nid: quiz-1\ntype: multiple-choice\nquestion: 예시 문항\noptions:\n  - 선택지 1\n  - 선택지 2\nanswerIndex: 0\n:::`
                                            }, `dialog-quiz-fallback-${index}`);
                                        }
                                        return /*#__PURE__*/ _jsxs("div", {
                                            style: {
                                                marginBottom: '32px'
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsx(InteractiveQuiz, {
                                                    quizData: segment.questions,
                                                    onSubmit: ()=>undefined
                                                }),
                                                /*#__PURE__*/ _jsx(QuizPreviewNotice, {
                                                    children: "실제 학습자와 동일한 인터랙티브 퀴즈 동작을 미리 확인할 수 있습니다."
                                                })
                                            ]
                                        }, `dialog-quiz-${index}`);
                                    }
                                    console.log('🔍 [MarkdownEditorMode] Rendering MarkdownRenderer (expanded):', {
                                        index,
                                        content: segment.content?.substring(0, 100),
                                        hasVideoDirective: segment.content?.includes(':::video'),
                                        segmentType: segment.type
                                    });
                                    return /*#__PURE__*/ _jsx(MarkdownRenderer, {
                                        markdown: segment.content
                                    }, `dialog-markdown-${index}`);
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
});
export default MarkdownEditorModeComponent;
